import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { liveAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const LiveEventsContext = createContext(null);
const POLL_MS = 15000;

/** Polls the Suite's webhook inbox so pages refresh themselves when the Hub pushes data. */
export function LiveEventsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState({ connected: false, webhookConfigured: false, lastEventAt: null });
  const subscribers = useRef(new Map());
  const since = useRef(null);

  const subscribe = useCallback((events, handler) => {
    const key = Symbol('subscriber');
    subscribers.current.set(key, { events, handler });
    return () => subscribers.current.delete(key);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    let primed = false;

    const poll = async () => {
      try {
        const res = await liveAPI.events(since.current);
        if (cancelled) return;
        const { data = [], server_time, webhook_configured } = res.data || {};
        since.current = server_time || since.current;
        setStatus(prev => ({
          connected: true,
          webhookConfigured: !!webhook_configured,
          lastEventAt: data.length > 0 ? data[data.length - 1].received_at : prev.lastEventAt,
        }));
        if (!primed) {
          primed = true; // ignore history on first poll, only react to new events
          return;
        }
        if (data.length > 0) {
          subscribers.current.forEach(({ events, handler }) => {
            const matching = data.filter(e => events.length === 0 || events.includes(e.event));
            if (matching.length > 0) handler(matching);
          });
        }
      } catch {
        if (!cancelled) setStatus(prev => ({ ...prev, connected: false }));
      }
    };

    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [isAuthenticated]);

  return (
    <LiveEventsContext.Provider value={{ subscribe, ...status }}>
      {children}
    </LiveEventsContext.Provider>
  );
}

/** Subscribe to Hub events. `events` = [] means every event. */
export function useLiveEvents(events, handler) {
  const context = useContext(LiveEventsContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!context) return undefined;
    return context.subscribe(events, (batch) => handlerRef.current(batch));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, events.join(',')]);

  return context || { connected: false, webhookConfigured: false, lastEventAt: null };
}

export const useLiveStatus = () => useContext(LiveEventsContext) || { connected: false, webhookConfigured: false };
